import { useState, useMemo, useCallback } from "react";
import { useClients } from "@/hooks/useClients";
import { useCreateClient } from "@/hooks/useCreateClient";
import { useUpdateClient } from "@/hooks/useUpdateClient";
import { useDeleteClient } from "@/hooks/useDeleteClient";
import { useCompanies } from "@/hooks/useCompanies";
import { OptimizedTable, TableColumn } from "@/components/ui/optimized-table";
import { ClientDialog } from "@/components/clients/ClientDialog";
import { ClientAdvancedFilter } from "@/components/clients/ClientAdvancedFilter";
import { ClientStatusStats } from "@/components/clients/ClientStatusStats";
import { Client } from "@/types/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { LoadingSpinnerWithText } from "@/components/ui/loading-spinner";
import { RefreshCw, Plus, Edit, Trash2, ChevronDown, ChevronUp, Building, FileText, Building2, Paperclip, Save, Users, Search } from "lucide-react";
import { ViewToggle } from "@/components/shared/ViewToggle";
import { ClientInlineFilter } from "@/components/clients/ClientInlineFilter";
import { Input } from "@/components/ui/input";
import { ClientsTable } from "@/components/clients/ClientsTable";
import { ClientsCardsView } from "@/components/clients/ClientsCardsView";
import { toast } from "sonner";
import { PaginationControls } from "@/components/clients/PaginationControls";
import { CompanySelector } from "@/components/clients/ClientDialog/CompanySelector";
import { AdditionalInfoTab } from "@/components/clients/ClientDialog/AdditionalInfoTab";
import { PassportInfoTab } from "@/components/clients/ClientDialog/PassportInfoTab";
import { AttachmentsTab } from "@/components/clients/ClientDialog/AttachmentsTab";
type FormData = Omit<Client, "id" | "created_at" | "updated_at" | "client_assignments">;
const getInitialFormData = (): FormData => ({
  name_ar: "",
  name_en: "",
  mobile: "",
  additional_info: "",
  passport_info: {},
  attachments: [],
  status: "waiting",
  client_type: "b2b",
  company_id: undefined,
  company: "",
  created_by: "",
  creator: undefined,
  companies: undefined,
  employees: undefined
});
export default function B2BClients() {
  const [selectedClient, setSelectedClient] = useState<Client | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [addFormData, setAddFormData] = useState<FormData>(getInitialFormData);
  const [activeTab, setActiveTab] = useState("company");
  const [view, setView] = useState<'table' | 'cards'>('table');
  const [filters, setFilters] = useState({
    name_ar: "",
    name_en: "",
    mobile: "",
    company: "",
    status: "",
    generalSearch: "",
    created_at_from: "",
    created_at_to: ""
  });
  const {
    data: clients = [],
    isLoading,
    error,
    refetch
  } = useClients();
  const {
    data: companies = []
  } = useCompanies();
  const createClientMutation = useCreateClient();
  const updateClientMutation = useUpdateClient();
  const deleteClientMutation = useDeleteClient();

  // تصفية عملاء B2B فقط
  const b2bClients = useMemo(() => {
    return clients.filter(client => client.client_type === 'b2b');
  }, [clients]);

  // فلترة العملاء بناءً على معايير البحث
  const filteredClients = useMemo(() => {
    return b2bClients.filter(client => {
      const matchesGeneralSearch = filters.generalSearch === "" || client.name_ar?.toLowerCase().includes(filters.generalSearch.toLowerCase()) || client.name_en?.toLowerCase().includes(filters.generalSearch.toLowerCase()) || client.mobile?.includes(filters.generalSearch) || client.company?.toLowerCase().includes(filters.generalSearch.toLowerCase());
      const matchesNameAr = filters.name_ar === "" || client.name_ar?.toLowerCase().includes(filters.name_ar.toLowerCase());
      const matchesNameEn = filters.name_en === "" || client.name_en?.toLowerCase().includes(filters.name_en.toLowerCase());
      const matchesMobile = filters.mobile === "" || client.mobile?.includes(filters.mobile);
      const matchesCompany = filters.company === "" || client.company?.toLowerCase().includes(filters.company.toLowerCase());
      const matchesStatus = filters.status === "" || client.status === filters.status;
      return matchesGeneralSearch && matchesNameAr && matchesNameEn && matchesMobile && matchesCompany && matchesStatus;
    });
  }, [b2bClients, filters]);

  // حساب إحصائيات الحالة
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    filteredClients.forEach(client => {
      stats[client.status] = (stats[client.status] || 0) + 1;
    });
    return stats;
  }, [filteredClients]);

  // خيارات الحالة للفلتر
  const statusOptions = [{
    value: 'confirmed',
    label: 'مؤكد',
    color: 'bg-green-100 text-green-800'
  }, {
    value: 'waiting',
    label: 'في الانتظار',
    color: 'bg-yellow-100 text-yellow-800'
  }, {
    value: 'in_progress',
    label: 'قيد التنفيذ',
    color: 'bg-blue-100 text-blue-800'
  }, {
    value: 'waiting_supplier',
    label: 'انتظار المورد',
    color: 'bg-orange-100 text-orange-800'
  }, {
    value: 'cancelled',
    label: 'ملغي',
    color: 'bg-red-100 text-red-800'
  }, {
    value: 'pricing_requested',
    label: 'طلب تسعير',
    color: 'bg-indigo-100 text-indigo-800'
  }, {
    value: 'pricing_sent',
    label: 'تم إرسال التسعير',
    color: 'bg-cyan-100 text-cyan-800'
  }, {
    value: 'booking_confirmed',
    label: 'تأكيد الحجز',
    color: 'bg-emerald-100 text-emerald-800'
  }, {
    value: 'traveling',
    label: 'يسافر',
    color: 'bg-purple-100 text-purple-800'
  }, {
    value: 'customer_service',
    label: 'خدمة العملاء',
    color: 'bg-pink-100 text-pink-800'
  }];
  const handleAddFormChange = useCallback((field: string, value: any) => {
    setAddFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);
  const handleAddFormSubmit = useCallback(async () => {
    try {
      if (!addFormData.company_id) {
        toast.error("يجب اختيار الشركة السياحية");
        return;
      }
      await createClientMutation.mutateAsync({
        ...addFormData,
        client_type: 'b2b',
        name_ar: addFormData.name_ar || `عميل B2B تابع لـ ${addFormData.company}`
      });
      toast.success("تم إضافة عميل B2B بنجاح");
      setAddFormData(getInitialFormData());
      setIsAddFormOpen(false);
      setActiveTab("company");
    } catch (error) {
      console.error("Error creating B2B client:", error);
      toast.error("حدث خطأ أثناء إضافة العميل");
    }
  }, [addFormData, createClientMutation]);
  const handleCompanyAdded = useCallback((newCompany: any) => {
    setAddFormData(prev => ({
      ...prev,
      company_id: newCompany.id,
      company: newCompany.name_ar,
      name_ar: `عميل B2B تابع لـ ${newCompany.name_ar}`
    }));
  }, []);
  const handleCompanyChange = useCallback((companyId: string | undefined) => {
    const selectedCompany = companies.find(c => c.id === companyId);
    setAddFormData(prev => ({
      ...prev,
      company_id: companyId,
      company: selectedCompany?.name_ar || "",
      name_ar: selectedCompany ? `عميل B2B تابع لـ ${selectedCompany.name_ar}` : ""
    }));
  }, [companies]);
  const handleEdit = useCallback((client: Client) => {
    setSelectedClient(client);
    setIsDialogOpen(true);
  }, []);
  const handleDelete = useCallback(async (client: Client) => {
    if (window.confirm(`هل أنت متأكد من حذف العميل ${client.name_ar}؟`)) {
      try {
        await deleteClientMutation.mutateAsync(client.id);
        toast.success("تم حذف العميل بنجاح");
      } catch (error) {
        console.error("Error deleting client:", error);
        toast.error("حدث خطأ أثناء حذف العميل");
      }
    }
  }, [deleteClientMutation]);
  const handleSuccess = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedClient(undefined);
    refetch();
  }, [refetch]);
  const getStatusColor = (status: string) => {
    const colors = {
      'confirmed': 'bg-green-100 text-green-800',
      'waiting': 'bg-yellow-100 text-yellow-800',
      'in_progress': 'bg-blue-100 text-blue-800',
      'waiting_supplier': 'bg-orange-100 text-orange-800',
      'cancelled': 'bg-red-100 text-red-800',
      'pricing_requested': 'bg-indigo-100 text-indigo-800',
      'pricing_sent': 'bg-cyan-100 text-cyan-800',
      'booking_confirmed': 'bg-emerald-100 text-emerald-800',
      'traveling': 'bg-purple-100 text-purple-800',
      'customer_service': 'bg-pink-100 text-pink-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };
  const getStatusLabel = (status: string) => {
    const statusOption = statusOptions.find(option => option.value === status);
    return statusOption ? statusOption.label : status;
  };
  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };
  const columns: TableColumn<Client>[] = [{
    key: "company",
    title: "الشركة السياحية",
    render: client => <div className="font-medium">{client.company || "-"}</div>
  }, {
    key: "name_ar",
    title: "اسم العميل",
    render: client => <div className="text-muted-foreground">{client.name_ar}</div>
  }, {
    key: "mobile",
    title: "رقم الجوال",
    render: client => <div className="text-muted-foreground">{client.mobile || "-"}</div>
  }, {
    key: "status",
    title: "الحالة",
    render: client => <Badge variant="outline">{client.status}</Badge>
  }, {
    key: "actions",
    title: "الإجراءات",
    render: client => <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleEdit(client)}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleDelete(client)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
  }];
  if (isLoading) {
    return <LoadingSpinnerWithText text="جاري تحميل عملاء B2B..." />;
  }
  if (error) {
    return <div className="text-center text-red-500">
        حدث خطأ أثناء تحميل العملاء: {error.message}
      </div>;
  }
  return <div className="container mx-auto p-6 space-y-6">
      {/* Header with controls */}
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold">عملاء B2B</h1>
        
        {/* زر الإضافة */}
        <Collapsible open={isAddFormOpen} onOpenChange={setIsAddFormOpen}>
          <CollapsibleTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              إضافة عميل B2B
              {isAddFormOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
        </Collapsible>
        
        {/* الفلتر المتقدم */}
        <ClientInlineFilter onFilterChange={handleFilterChange} statusOptions={statusOptions} currentFilters={filters} />
        
        {/* أزرار العرض */}
        <ViewToggle view={view} onViewChange={setView} />
        
        {/* البحث السريع */}
        <div className="relative flex-1 mr-auto">
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input placeholder="البحث السريع..." value={filters.generalSearch} onChange={e => handleFilterChange({
          ...filters,
          generalSearch: e.target.value
        })} className="pr-10" />
        </div>
      </div>

      {/* إحصائيات الحالة */}
      <ClientStatusStats statusStats={statusStats} getStatusLabel={getStatusLabel} getStatusColor={getStatusColor} className="mb-6" />

      <Card>
        
        <CardContent className="pb-0">
          {/* View Content */}
          {view === 'table' ? <ClientsTable clients={filteredClients.slice((currentPage - 1) * pageSize, currentPage * pageSize)} onEditClient={handleEdit} onDeleteClient={(clientId: string) => {
          const client = filteredClients.find(c => c.id === clientId);
          if (client) handleDelete(client);
        }} onAddClient={() => setIsAddFormOpen(true)} /> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <ClientsCardsView clients={filteredClients.slice((currentPage - 1) * pageSize, currentPage * pageSize)} onEditClient={handleEdit} onDeleteClient={(clientId: string) => {
            const client = filteredClients.find(c => c.id === clientId);
            if (client) handleDelete(client);
          }} />
            </div>}

          {/* Empty State */}
          {filteredClients.length === 0 && b2bClients.length > 0 && <Card className="text-center py-12">
              <CardContent>
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">لا توجد نتائج</h3>
                <p className="text-muted-foreground mb-4">لا توجد عملاء تطابق معايير البحث</p>
                <Button variant="outline" onClick={() => setFilters({
              name_ar: "",
              name_en: "",
              mobile: "",
              company: "",
              status: "",
              generalSearch: "",
              created_at_from: "",
              created_at_to: ""
            })}>
                  مسح جميع الفلاتر
                </Button>
              </CardContent>
            </Card>}
          
          {b2bClients.length === 0 && <Card className="text-center py-12">
              <CardContent>
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">لا يوجد عملاء B2B</h3>
                <p className="text-muted-foreground mb-4">ابدأ بإضافة عميل B2B جديد</p>
                <Button onClick={() => setIsAddFormOpen(true)}>
                  <Plus className="w-4 h-4 ml-2" />
                  إضافة عميل B2B جديد
                </Button>
              </CardContent>
            </Card>}
        </CardContent>
        <div className="px-6 pb-4">
          <PaginationControls
            currentPage={currentPage}
            totalPages={Math.ceil(filteredClients.length / pageSize)}
            pageSize={pageSize}
            totalItems={filteredClients.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </div>
      </Card>

      <ClientDialog client={selectedClient} open={isDialogOpen} onOpenChange={setIsDialogOpen} onSuccess={handleSuccess} trigger={<></>} />
    </div>;
}